import React, { useEffect, useRef } from 'react';

function hexToRgb(hex) {
    const normalized = hex.replace('#', '');
    const bigint = parseInt(normalized, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
}

function fillAccent(accent, alpha) {
    return `rgba(${accent.r}, ${accent.g}, ${accent.b}, ${alpha})`;
}

function drawGrid(ctx, w, h, accent, t) {
    ctx.strokeStyle = fillAccent(accent, 0.08);
    ctx.lineWidth = 1;
    const spacing = Math.max(22, Math.min(w, h) / 14);
    const offset = (t * 12) % spacing;
    for (let x = -spacing; x < w + spacing; x += spacing) {
        ctx.beginPath();
        ctx.moveTo(x + offset, 0);
        ctx.lineTo(x + offset, h);
        ctx.stroke();
    }
    for (let y = -spacing; y < h + spacing; y += spacing) {
        ctx.beginPath();
        ctx.moveTo(0, y + offset * 0.6);
        ctx.lineTo(w, y + offset * 0.6);
        ctx.stroke();
    }
}

/** Point on a rectangle perimeter, u in [0, 1) around the four sides. */
function pointOnRectPerimeter(cx, cy, half, u) {
    const perim = half * 8;
    let d = ((u % 1) + 1) % 1 * perim;
    if (d < half * 2) return { x: cx - half + d, y: cy - half };
    d -= half * 2;
    if (d < half * 2) return { x: cx + half, y: cy - half + d };
    d -= half * 2;
    if (d < half * 2) return { x: cx + half - d, y: cy + half };
    d -= half * 2;
    return { x: cx - half, y: cy + half - d };
}

function drawDot(ctx, accent, x, y, radius = 2.2, alpha = 0.8) {
    ctx.fillStyle = fillAccent(accent, alpha);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
}

function animateTower(ctx, w, h, accent, t) {
    const columns = Math.max(10, Math.floor(w / 36));
    const rowStep = Math.max(14, h / 18);
    const padY = 12;

    for (let c = 0; c < columns; c += 1) {
        const x = ((c + 0.5) / columns) * w;
        const levels = 3 + Math.abs((c % 4) - 1);
        for (let l = 0; l < levels; l += 1) {
            const phase = t * 1.4 + c * 0.35 + l * 0.5;
            const y = h - padY - l * rowStep - Math.sin(phase) * 8;
            const alpha = 0.2 + (l / levels) * 0.6;
            const radius = 3 + (l / levels) * 5;
            ctx.fillStyle = fillAccent(accent, alpha);
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
            if (l < levels - 1) {
                ctx.strokeStyle = fillAccent(accent, alpha * 0.7);
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.moveTo(x, y - radius);
                ctx.lineTo(x, y - rowStep + radius);
                ctx.stroke();
            }
        }
        const particleY = h - ((t * 55 + c * 37) % (h + 50));
        ctx.fillStyle = fillAccent(accent, 0.9);
        ctx.beginPath();
        ctx.arc(x, particleY, 3, 0, Math.PI * 2);
        ctx.fill();
    }

    for (let band = 0; band < 4; band += 1) {
        const y = (h * (band + 1)) / 5 + Math.sin(t * 0.9 + band) * 6;
        ctx.strokeStyle = fillAccent(accent, 0.08 + band * 0.04);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
    }
}

function animateConverge(ctx, w, h, accent, t) {
    const focalPoints = [
        [w * 0.1, h * 0.1],
        [w * 0.28, h * 0.32],
        [w * 0.72, h * 0.28],
        [w * 0.5, h * 0.72],
        [w * 0.18, h * 0.78],
        [w * 0.82, h * 0.68],
        [w * 0.9, h * 0.3],
    ];
    const maxReach = Math.hypot(w, h) * 0.55;

    focalPoints.forEach(([cx, cy], fp) => {
        const count = 36;
        // Make cx, cy slightly dynamic for a more organic "moving focal points" effect
        // Instead of static positions, modulate cx and cy with gentle sine/cosine shifts
        const tPrime = t + fp * 0.93;
        const dynamicCx = cx + Math.sin(tPrime * 0.45 + fp) * 12 + Math.cos(tPrime * 0.23 + fp) * 8;
        const dynamicCy = cy + Math.cos(tPrime * 0.38 + fp * 1.19) * 10 + Math.sin(tPrime * 0.13 + fp) * 7;
        // Use dynamicCx/dynamicCy below (for particles and the arc)
        for (let i = 0; i < count; i += 1) {
            const angle = (i / count) * Math.PI * 2 + t * 0.45 + fp;
            const radius = 24 + ((t * 38 + i * 11 + fp * 40) % maxReach);
            const shrink = 1 - radius / maxReach;
            const x = dynamicCx + Math.cos(angle) * radius;
            const y = dynamicCy + Math.sin(angle) * radius;
            if (x < 0 || x > w || y < 0 || y > h) continue;
            ctx.fillStyle = fillAccent(accent, 0.12 + shrink * 0.75);
            ctx.beginPath();
            ctx.arc(x, y, 1.5 + shrink * 3, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.strokeStyle = fillAccent(accent, 0.3);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(dynamicCx, dynamicCy, 10 + Math.sin(t * 2 + fp) * 5, 0, Math.PI * 2);
        ctx.stroke();
    });

    for (let i = 0; i < 24; i += 1) {
        const progress = (t * 0.35 + i * 0.07) % 1;
        const edge = i % 4;
        let x;
        let y;
        const targetX = w * (0.3 + (i % 5) * 0.1);
        const targetY = h * (0.25 + (i % 4) * 0.15);
        if (edge === 0) {
            x = progress * targetX;
            y = 0;
        } else if (edge === 1) {
            x = w;
            y = progress * targetY;
        } else if (edge === 2) {
            x = w - progress * (w - targetX);
            y = h;
        } else {
            x = 0;
            y = h - progress * (h - targetY);
        }
        ctx.fillStyle = fillAccent(accent, 0.35 * (1 - progress));
        ctx.fillRect(x - 1, y - 1, 3, 3);
    }
}

function animateFactorial(ctx, w, h, accent, t) {
    const cols = 3;
    const rows = 3;
    const randomShiftx = Math.random()*0.1-0.5;
    const randomShifty = Math.random()*0.1-0.5;
    for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
            // Induce some random x, y shift to cx, cy respectively
            const baseCx = ((col + 0.5 ) / cols) * w;
            const baseCy = ((row + 0.5 ) / rows) * h;
            // Use a simple deterministic pseudo-random shift per col/row/t for animation consistency
            const cx =
                baseCx + randomShiftx +
                (Math.sin(t * 0.8 + col * 1.2 + row * 0.34) * w * 0.05);
            const cy =
                baseCy + randomShifty +
                (Math.cos(t * 0.6 + row * 1.6 + col * 0.51) * h * 0.07);
 
            const maxRadius = Math.min(w / cols, h / rows) * 0.42;

            for (let n = 0; n < 6; n += 1) {
                const phase = t * 1.8 - n * 0.35 + (row + col) * 0.4;
                const pulse = Math.max(0, Math.sin(phase));
                const radius = maxRadius * (0.15 + (n / 6) * 0.85) + pulse * 8;
                ctx.strokeStyle = fillAccent(accent, 0.06 + pulse * 0.3);
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(cx, cy, radius, 0, Math.PI * 2);
                ctx.stroke();

                const dots = n + 3;
                for (let d = 0; d < dots; d += 1) {
                    const angle = (d / dots) * Math.PI * 2 + t * (0.4 + n * 0.05);
                    const x = cx + Math.cos(angle) * radius;
                    const y = cy + Math.sin(angle) * radius;
                    ctx.fillStyle = fillAccent(accent, 0.25 + pulse * 0.65);
                    ctx.fillRect(x - 2, y - 2, 4, 4);
                }
            }
        }
    }
}

function animateHarmonic(ctx, w, h, accent, t) {
    const baseY = h / 2;
    const maxAmp = h * 0.2;
    ctx.lineWidth = 2;
    for (let k = 1; k <= 9; k += 1) {
        const amp = maxAmp / (k * k * 0.5);
        const phase = t * (0.6 + k * 0.1);
        const midY = baseY + .1 * w * Math.sin(.1 * k * phase - 1.5);
        ctx.strokeStyle = fillAccent(accent, 0.12 + (8 - k) * 0.1);
        ctx.beginPath();
        for (let x = 0; x <= w; x += 2) {
            const y = midY + amp * Math.sin((x / w) * Math.PI * 2 * k + phase);
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Particles traveling along each harmonic curve (slow, soothing)
        const particlesOnCurve = 3 + (k % 3);
        for (let p = 0; p < particlesOnCurve; p += 1) {
            const u = (t * (0.04 + k * 0.007) + p / particlesOnCurve + k * 0.07) % 1;
            const x = u * w;
            const y = midY + amp * Math.sin(u * Math.PI * 2 * k + phase);
            drawDot(ctx, accent, x, y, 2 + (k === 1 ? 1 : 0), 0.55 + (7 - k) * 0.05);
        }
    }

    for (let i = 0; i < 12; i += 1) {
        const x = (i / 11) * w;
        const sway = Math.sin(t * 1.2 + i) * 8;
        ctx.strokeStyle = fillAccent(accent, 0.06);
        ctx.beginPath();
        ctx.moveTo(x + sway, 0);
        ctx.lineTo(x - sway, h);
        ctx.stroke();
    }
}

function animateAlternate(ctx, w, h, accent, t) {
    // Tessellated alternating triangles (left/right pulse) covering the full canvas.
    const cols = Math.max(7, Math.floor(w / 36));
    const rows = Math.max(5, Math.floor(h / 28));
    const cellW = w / cols;
    const cellH = h / rows;
    const pulseA = 0.22 + 0.18 * Math.sin(t * 2.1);
    const pulseB = 0.22 + 0.18 * Math.sin(t * 2.1 + Math.PI);

    for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
            const x0 = col * cellW;
            const y0 = row * cellH;
            const x1 = x0 + cellW;
            const y1 = y0 + cellH;
            const flip = (row + col) % 2 === 0;
            const phaseShift = (row * 0.15 + col * 0.11);
            const bright = flip
                ? pulseA + 0.08 * Math.sin(t * 1.5 + phaseShift)
                : pulseB + 0.08 * Math.sin(t * 1.5 + phaseShift + Math.PI);

            // Upper triangle
            ctx.fillStyle = fillAccent(accent, Math.max(0.05, bright));
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.lineTo(x1, y0);
            ctx.lineTo(flip ? x0 : x1, y1);
            ctx.closePath();
            ctx.fill();

            // Lower triangle (complementary tint)
            ctx.fillStyle = fillAccent(accent, Math.max(0.04, bright * 0.55));
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x0, y1);
            ctx.lineTo(flip ? x1 : x0, y0);
            ctx.closePath();
            ctx.fill();

            ctx.strokeStyle = fillAccent(accent, 0.12);
            ctx.lineWidth = 0.6;
            ctx.strokeRect(x0, y0, cellW, cellH);
        }
    }

    // Moving seam lines that reinforce the tessellation
    for (let i = 0; i < cols + rows; i += 1) {
        const offset = (t * 36 + i * 22) % (w + h);
        ctx.strokeStyle = fillAccent(accent, 0.1);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(offset, 0);
        ctx.lineTo(0, offset);
        ctx.stroke();
    }

    // Particles on ~70% of tessellated tiles (3–4 edge walkers each)
    for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
            const tileId = row * cols + col;
            // Stable ~70% selection: skip when (tileId * 7) % 10 >= 7
            if ((tileId * 7) % 10 >= 7) continue;

            const x0 = col * cellW;
            const y0 = row * cellH;
            const x1 = x0 + cellW;
            const y1 = y0 + cellH;
            const flip = (row + col) % 2 === 0;
            const particlesInTile = 3 + (tileId % 2);

            for (let p = 0; p < particlesInTile; p += 1) {
                const u = (t * (0.2 + (p % 3) * 0.04) + tileId * 0.09 + p / particlesInTile) % 1;
                let px;
                let py;
                if (u < 1 / 3) {
                    const s = u * 3;
                    px = x0 + (x1 - x0) * s;
                    py = y0;
                } else if (u < 2 / 3) {
                    const s = (u - 1 / 3) * 3;
                    const ax = x1;
                    const ay = y0;
                    const bx = flip ? x0 : x1;
                    const by = y1;
                    px = ax + (bx - ax) * s;
                    py = ay + (by - ay) * s;
                } else {
                    const s = (u - 2 / 3) * 3;
                    const ax = flip ? x0 : x1;
                    const ay = y1;
                    px = ax + (x0 - ax) * s;
                    py = ay + (y0 - ay) * s;
                }
                drawDot(ctx, accent, px, py, 1.8 + (p % 2) * 0.4, 0.7 + (p % 3) * 0.08);
            }
        }
    }
}

function drawFragmentedSquare(ctx, accent, cx, cy, size, t, layerSeed) {
    const half = size / 2;
    const gap = 0.1 + (layerSeed % 3) * 0.05;
    const jitter = Math.sin(t * 1.3 + layerSeed * 1.7) * 3;
    const alpha = 0.14 + (layerSeed % 5) * 0.05;
    ctx.strokeStyle = fillAccent(accent, alpha);
    ctx.lineWidth = 1.4;

    const sides = [
        [cx - half + jitter, cy - half, cx + half * (0.5 - gap) + jitter, cy - half],
        [cx + half * (0.5 + gap) + jitter, cy - half, cx + half + jitter, cy - half],
        [cx + half, cy - half + jitter, cx + half, cy - half * gap + jitter],
        [cx + half, cy + half * gap + jitter, cx + half, cy + half + jitter],
        [cx + half - jitter, cy + half, cx - half * (0.5 - gap) - jitter, cy + half],
        [cx - half * (0.5 + gap) - jitter, cy + half, cx - half - jitter, cy + half],
        [cx - half, cy + half - jitter, cx - half, cy + half * gap - jitter],
        [cx - half, cy - half * gap - jitter, cx - half, cy - half - jitter],
    ];
    sides.forEach(([x0, y0, x1, y1]) => {
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
    });

    // Particles walking the square perimeter (slow, soothing)
    const particleCount = 2 + (layerSeed % 3);
    for (let p = 0; p < particleCount; p += 1) {
        const u = (t * (0.055 + (layerSeed % 4) * 0.01) + p / particleCount + layerSeed * 0.13) % 1;
        const pt = pointOnRectPerimeter(cx, cy, half, u);
        drawDot(ctx, accent, pt.x, pt.y, 2.2, 0.8);
    }
}

function animateNested(ctx, w, h, accent, t) {
    // Multiple nested square families at different positions and scales across the canvas.
    const clusters = [
        { cx: w * 0.5, cy: h * 0.5, scale: 0.92, layers: 7 },
        { cx: w * 0.18, cy: h * 0.22, scale: 0.34, layers: 5 },
        { cx: w * 0.82, cy: h * 0.2, scale: 0.28, layers: 4 },
        { cx: w * 0.2, cy: h * 0.78, scale: 0.3, layers: 5 },
        { cx: w * 0.78, cy: h * 0.74, scale: 0.36, layers: 5 },
        { cx: w * 0.05, cy: h * 0.5, scale: 0.22, layers: 4 },
        { cx: w * 0.92, cy: h * 0.5, scale: 0.24, layers: 4 },
    ];
    const minDim = Math.min(w, h);

    clusters.forEach((cluster, ci) => {
        const base = minDim * cluster.scale;
        for (let i = 0; i < cluster.layers; i += 1) {
            const size = base * (1 - i / (cluster.layers + 0.4)) + Math.sin(t * 0.9 + i + ci) * 4;
            drawFragmentedSquare(ctx, accent, cluster.cx, cluster.cy, size, t, i + ci * 11);
        }
    });
}

function animateRadical(ctx, w, h, accent, t) {
    const anchors = [
        [w * 0.5, h * 0.5],
        [w * 0.22, h * 0.28],
        [w * 0.78, h * 0.72],
        [w * 0.1, h * 0.75],
        [w * 0.9, h * 0.25],
    ];
    anchors.forEach(([cx, cy], a) => {
        let size = Math.min(w, h) * (a === 0 ? 0.88 : 0.42);
        for (let depth = 0; depth < 7; depth += 1) {
            const half = size / 2;
            const alpha = 0.1 + depth * 0.08;
            ctx.strokeStyle = fillAccent(accent, alpha);
            ctx.lineWidth = 2;
            ctx.strokeRect(cx - half, cy - half, size, size);
            const inset = size * 0.16;
            ctx.beginPath();
            ctx.moveTo(cx - half, cy - half);
            ctx.lineTo(cx - half + inset, cy - half + inset);
            ctx.stroke();

            // Particles on each nested radical square
            const u = (t * (0.2 + depth * 0.005) + a * 0.2 + depth * 0.15) % 1;
            const pt = pointOnRectPerimeter(cx, cy, half, u);
            drawDot(ctx, accent, pt.x, pt.y, 2.2, 0.75);

            size *= 0.6 + Math.sin(t * 0.8 + depth + a) * 0.02;
        }
    });
}

function animateOrbit(ctx, w, h, accent, t) {
    const centers = [
        [w * 0.1, h * 0.15],
        [w * 0.45, h * 0.15],
        [w * 0.75, h * 0.25],
        [w * 0.45, h * 0.55],
        [w * 0.15, h * 0.7],
        [w * 0.7, h * 0.7],
        [w * 0.9, h * 0.82],
    ];
    const primes = [2, 5, 7, 11, 13, 17, 23, 29, 37, 41, 47];
    const maxOrbit = Math.min(w, h) * 0.18;

    centers.forEach(([cx, cy], ci) => {
        primes.forEach((p, i) => {
            const angle = t * (0.35 + i * 0.04) + i + ci;
            const radius = maxOrbit * (0.35 + (i / primes.length) * 0.75);
            // Induce some dynamic pseudo-random shift similar to animateFactorial for more organic orbits
            const shiftX = Math.sin(t * 0.5 + i * 1.17 + ci * 0.21) * 7;
            const shiftY = Math.cos(t * 0.42 + i * 1.33 + ci * 0.13) * 6;
            const x = cx + Math.cos(angle) * radius + shiftX;
            const y = cy + Math.sin(angle) * radius * 0.8 + shiftY;
    
            ctx.fillStyle = fillAccent(accent, 0.55 + (i % 3) * 0.12);
            ctx.beginPath();
            ctx.arc(x, y, 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = fillAccent(accent, 0.1);
            ctx.beginPath();
            ctx.ellipse(x, y, radius, radius * 0.7, 0, 0, Math.PI * 2);
            ctx.stroke();
            void p;
        });
    });
}

function animatePolynomial(ctx, w, h, accent, t) {
    const ringCenters = [w * 0.2, w * 0.5, w * 0.8];
    const maxRing = h * 0.38;

    ringCenters.forEach((cx, ci) => {
        for (let n = 0; n < 5; n += 1) {
            const radius = maxRing * (0.2 + n * 0.16) + Math.sin(t * 1.5 + n + ci) * 8;
            ctx.strokeStyle = fillAccent(accent, 0.1 + n * 0.1);
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(cx, h / 2, radius, 0, Math.PI * 2);
            ctx.stroke();

            // Particles orbiting each polynomial ring (slow, soothing)
            const angle = t * (0.22 + n * 0.05) + ci + n;
            drawDot(
                ctx,
                accent,
                cx + Math.cos(angle) * radius,
                h / 2 + Math.sin(angle) * radius,
                2.2,
                0.8
            );
        }
    });

    for (let band = 0; band < 3; band += 1) {
        const baseY = h * (0.25 + band * 0.22);
        ctx.strokeStyle = fillAccent(accent, 0.45 - band * 0.1);
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 2) {
            const nx = (x / w) * 2.4 - 1.2;
            const y = baseY - Math.exp(nx * 0.9) * (h * 0.06) * Math.sin(t * 0.4 + nx * 2 + band);
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Particles traveling along each exponential curve band (slow, soothing)
        for (let p = 0; p < 3; p += 1) {
            const u = (t * (0.05 + band * 0.01) + p / 3 + band * 0.2) % 1;
            const x = u * w;
            const nx = (x / w) * 2.4 - 1.2;
            const y = baseY - Math.exp(nx * 0.9) * (h * 0.06) * Math.sin(t * 0.4 + nx * 2 + band);
            drawDot(ctx, accent, x, y, 2, 0.75);
        }
    }
}

const ANIMATORS = {
    tower: animateTower,
    converge: animateConverge,
    factorial: animateFactorial,
    harmonic: animateHarmonic,
    alternate: animateAlternate,
    nested: animateNested,
    radical: animateRadical,
    orbit: animateOrbit,
    polynomial: animatePolynomial,
};

export default function FuturisticMathCanvas({ variant = 'tower', accentColor = '#2cbb5d', active = true }) {
    const canvasRef = useRef(null);
    const frameRef = useRef(null);
    const startRef = useRef(performance.now());

    useEffect(() => {
        if (!active) return undefined;

        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        const accent = hexToRgb(accentColor);
        const animator = ANIMATORS[variant] || animateTower;

        const render = (now) => {
            const t = (now - startRef.current) / 1000;
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            drawGrid(ctx, width, height, accent, t);
            animator(ctx, width, height, accent, t);
            frameRef.current = requestAnimationFrame(render);
        };

        frameRef.current = requestAnimationFrame(render);
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [variant, accentColor, active]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const resize = () => {
            const parent = canvas.parentElement;
            if (!parent) return;
            canvas.width = parent.clientWidth;
            canvas.height = parent.clientHeight;
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(canvas.parentElement);
        return () => observer.disconnect();
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }}
        />
    );
}
